/*
 * Boston Proper × GA4 / GTM event helper.
 *
 * Ported from Atlas's src/scripts/core/helpers/trackGtm.js — same
 * payload shape so the existing GTM tags + GA4 dashboards keyed off
 * the Atlas event names keep working without re-mapping.
 *
 * Public API on window.bpTrack:
 *   view_item(variant, product, eventLocation)
 *   view_item_list(items, listName, listId, locationId, startIndex, eventLocation, searchTerm)
 *   select_item(product, listName, index)
 *   add_to_cart(variant, product, available, eventLocation, parentProductId)
 *   remove_from_cart(item, eventLocation)
 *   view_cart()
 *   size_change(sizeValue, eventLocation)
 *   color_change(colorValue, eventLocation)        // Lana-new, not in Atlas
 *   add_to_wishlist(product, eventLocation)        // Lana-new
 *   remove_from_wishlist(product, eventLocation)   // Lana-new
 *   sign_up(method)
 *   login(method)
 *   search(query, results_count)                   // Lana-new
 *   filter_apply(filters, listName)                // Lana-new
 *   sort_change(sortBy, listName)                  // Lana-new
 *
 * Plus internals exposed for the page tracker + Shopify pixel:
 *   _push(eventName, payload)         — central dataLayer dispatcher
 *   _userDetails()                    — builds the user_details object
 *   _eventDetails(eventLocation)      — builds the event_details object
 *   _itemsArray(products, listName)   — builds the GA4 items[] array
 *   _saveListContext(product, name)   — writes ga4_item_list to localStorage
 *   _getListContext(variant)          — reads ga4_item_list from localStorage
 *
 * Atlas-compat naming: snake_case method names match Atlas exactly so
 * downstream GTM triggers don't need re-binding.
 */

(function () {
  'use strict';

  if (window.bpTrack) return; // idempotent

  // -------------------------------------------------------------------
  // Constants — match Atlas's GA4 currency + Shopify defaults
  // -------------------------------------------------------------------

  var CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  var LIST_STORAGE_KEY = 'ga4_item_list';
  var ITEMS_MAX = 20; // Atlas caps view_item_list at 20

  // -------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------

  function _now() { return Date.now(); }

  function _eventId() {
    // millis + nanos-ish (random suffix) — matches Atlas's event_id shape.
    return _now() + '-' + Math.floor(Math.random() * 1e9);
  }

  // event_details — Atlas shape (trackGtm.js createEventDetails):
  // { event_location, timestamp (string ms), event_id }.
  function _eventDetails(eventLocation) {
    return {
      event_location: eventLocation || _locationFromPath(),
      timestamp:      String(_now()),
      event_id:       _eventId()
    };
  }

  // user_details — spec shape, built centrally in bp-analytics-globals
  // (localStorage gtm_user_data backup + client-side SHA-256). Read the
  // cached async result when ready, else the synchronous interim; both
  // are the same spec shape (user_id = Peak id [null for now],
  // customer_id = Shopify id, customer_type / orders_count /
  // is_first_order, nested enhanced_conversion_data + user_hashed_data).
  function _userDetails() {
    if (window.__bp_user_details__) return window.__bp_user_details__;
    if (typeof window.bpUserDetailsSync === 'function') return window.bpUserDetailsSync();
    // Last-resort fallback if the globals snippet didn't load.
    var cid = window.__customer_id__;
    return {
      log_state:                cid ? 'Logged In' : 'Logged Out',
      user_id:                  null,
      customer_id:              cid ? String(cid) : null,
      customer_type:            window.__customer_type__ || 'new',
      orders_count:             window.__orders_count__ != null ? Number(window.__orders_count__) : null,
      is_first_order:           null,
      enhanced_conversion_data: window.__enhanced_conversion_data__ || {},
      user_hashed_data:         window.__user_hashed_data__ || {}
    };
  }

  function _locationFromPath() {
    var p = window.location.pathname;
    if (p === '/' || p === '') return 'Homepage';
    if (p.indexOf('/products/')    === 0) return 'Product Detail';
    if (p.indexOf('/collections/') === 0) return 'Site Browse';
    if (p.indexOf('/search')       === 0) return 'Search Results';
    if (p.indexOf('/cart')         === 0) return 'Cart';
    if (p.indexOf('/blogs/')       === 0) return 'Blog';
    if (p.indexOf('/pages/')       === 0) return 'Page';
    return 'Other';
  }

  function _push(eventName, ecommerce) {
    window.dataLayer = window.dataLayer || [];
    // "If we can't get it, hide it": omit empty-string item fields rather than
    // emitting "" for values we couldn't resolve (e.g. item_shopify_variant /
    // item_category / item_stock_qty on sources that lack them — BR grid cards).
    // Intentional null values (user_details, item_category4/5) are preserved.
    if (ecommerce && Array.isArray(ecommerce.items)) {
      ecommerce.items.forEach(function (it) {
        if (it && typeof it === 'object') {
          Object.keys(it).forEach(function (k) { if (it[k] === '') delete it[k]; });
        }
      });
    }
    // Clear the prior ecommerce object first (Atlas does this before every
    // GA4 push, trackGtm.js). Without it, keys from the previous event
    // bleed into the next one in GA4.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: ecommerce });
    if (window.__DL__) {
      window.__DL__.lastEvent = eventName;
    }
  }

  // Enrich items[] with live inventory (item_stock_qty) via the Storefront
  // `quantityAvailable` lookup, THEN push. Cart items carry item_shopify_variant
  // (the variant id), so the CART events can resolve the real stock number that
  // cart.js omits. Falls back to a plain _push (blank qty) when the helper is
  // absent, no variant ids are present, or inventory can't be read — never
  // throws, and only ever waits on one batched fetch.
  function _pushWithInventory(eventName, ecommerce) {
    var items = (ecommerce && ecommerce.items) || [];
    // Only look up items that are MISSING a stock qty (initial PDP load already
    // has it from Liquid — no extra call) AND carry a variant id to query by
    // (the swap view_item + cart events do). No candidates → plain push.
    var need = items.filter(function (it) {
      return (it.item_stock_qty === '' || it.item_stock_qty == null) && it.item_shopify_variant;
    });
    var helper = window.bpColorSiblings && window.bpColorSiblings.fetchInventory;
    if (!need.length || !helper) { _push(eventName, ecommerce); return; }
    helper(need.map(function (it) { return it.item_shopify_variant; })).then(function (map) {
      need.forEach(function (it) {
        var d = map[String(it.item_shopify_variant)];
        if (!d) return;
        // Only fill the qty — do NOT touch item_stock_message: a line with
        // inventory_policy "continue" can be oversold (q <= 0) yet still be
        // purchasable / "In Stock", so leave the message the caller set.
        if (d.qty != null) it.item_stock_qty = d.qty;
        // Sale fields (GA4 QA #2): cart.js carries no compare-at price, so a
        // sold-on-sale line looked "Full Price". When the variant's compare-at
        // exceeds its sold price, mark Sale Price + fill the original price and
        // the discount amount, matching the PDP/PLP sale shape.
        if (d.compareAt != null && d.price != null && d.compareAt > d.price) {
          it.item_category3 = 'Sale Price';
          it.item_original_price = Number(d.compareAt.toFixed(2));
          it.item_sale_discount = Number((d.compareAt - d.price).toFixed(2));
        }
      });
    }).catch(function () { /* keep blank qty */ }).then(function () {
      _push(eventName, ecommerce);
    });
  }

  // -------------------------------------------------------------------
  // list-context attribution (so add_to_cart from a card can carry
  // the item_list_name / item_list_id of the surface the user came
  // from). Mirrors Atlas's localStorage attribution.
  // -------------------------------------------------------------------

  function _saveListContext(product, listName, listId) {
    if (!product || !product.id) return;
    try {
      var ctx = {
        product_id: String(product.id),
        list_name:  listName || _locationFromPath(),
        list_id:    listId   || ''
      };
      localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify(ctx));
    } catch (_) { /* private mode / storage full — silently ignore */ }
  }

  function _getListContext(variant) {
    try {
      var raw = localStorage.getItem(LIST_STORAGE_KEY);
      if (!raw) return null;
      var ctx = JSON.parse(raw);
      // Only return when the stored product matches the variant's parent
      // — stale context from a previous PDP shouldn't leak into a new ATC.
      if (!variant || !variant.product_id) return ctx;
      if (String(variant.product_id) === ctx.product_id) return ctx;
      return null;
    } catch (_) { return null; }
  }

  // -------------------------------------------------------------------
  // GA4 items[] builders
  // -------------------------------------------------------------------

  // Atlas reads BP_ProductAttributes.bp_department for item_category
  // and slices SKU into inv5 / inv7 / inv10. Replicated 1:1.
  function _skuParts(sku) {
    var s = String(sku || '');
    return {
      sku:        s,
      inv5:       s.slice(0, 5),
      inv7:       s.slice(0, 7),
      inv10:      s.slice(0, 10),
      parent_id:  s.slice(0, 7)
    };
  }

  // Item shape mirrors Atlas's trackGtm.js items[] 1:1 so the
  // GTM-NS3X79G container variables resolve. Notably: item_id is the
  // inv7 SKU (NOT the Shopify product id), item_variant is the full SKU,
  // item_parent_id is inv5, and item_shopify_id / item_shopify_variant /
  // location_id / item_stock_* / item_original_price are all present.
  function _itemFromProduct(product, opts) {
    opts = opts || {};
    if (!product) return null;
    var variant = opts.variant || product.selected_variant || (product.variants && product.variants[0]) || {};
    var parts = _skuParts(variant.sku || product.sku || '');
    var price = (variant.price != null ? variant.price : product.price) || 0;
    if (typeof price === 'string') price = parseFloat(price);
    if (price > 1000) price = price / 100;            // Shopify cents → dollars
    var compare = variant.compare_at_price || product.compare_at_price || 0;
    if (typeof compare === 'string') compare = parseFloat(compare);
    if (compare && compare > 1000) compare = compare / 100;
    var onSale = compare && compare > price;
    var saleDiscount = onSale ? Number((compare - price).toFixed(2)) : 0;

    var category = (product.metafields && product.metafields.BP_ProductAttributes && product.metafields.BP_ProductAttributes.bp_department) ||
                   product.product_type || variant.category || '';

    var location = opts.eventLocation || opts.listName || _locationFromPath();
    var stockQty = (variant.inventory_quantity != null) ? variant.inventory_quantity : '';
    var inStock = (variant.available === true) || (typeof stockQty === 'number' && stockQty > 0);

    var item = {
      item_id:              parts.inv7,                       // inv7 SKU
      item_shopify_id:      String(product.id || ''),
      item_shopify_variant: variant.id != null ? String(variant.id) : '',
      item_name:            product.title || '',
      affiliation:          opts.affiliation || 'Boston Proper',
      currency:             CURRENCY,
      discount:             0,
      index:                opts.index != null ? opts.index : 0,
      item_brand:           product.vendor || 'Boston Proper',
      item_category:        category,
      item_category2:       opts.collectionTitle || undefined,
      item_category3:       onSale ? 'Sale Price' : 'Full Price',
      item_category4:       variant.option1 || null,          // Color
      item_category5:       variant.option2 || null,          // Size
      item_variant:         parts.sku,                        // full SKU
      // item_list_id tracks the SURFACE (location_id) so both name the same
      // experience — Product Listing / Quickview / Cart Quickview / Cart Flyout
      // (GA4 QA #2). item_list_name stays the browse/search label.
      item_list_id:         opts.listId || opts.locationId || location,
      item_list_name:       opts.listName || 'Site Browse',
      item_parent_id:       parts.inv5,                       // inv5 SKU
      location_id:          opts.locationId || location,
      price:                Number((price || 0).toFixed(2)),
      item_original_price:  onSale ? Number(compare.toFixed(2)) : Number((price || 0).toFixed(2)),
      quantity:             opts.quantity || 1,
      item_stock_message:   inStock ? 'In Stock' : 'Out of Stock',
      item_stock_qty:       stockQty,
      item_sale_discount:   saleDiscount
    };

    return item;
  }

  // Builds the items[] for ONE view_item_list event (one chunk). `startIndex`
  // is the position of this chunk's first product in the FULL list, so the
  // GA4 `index` is 0-based AND continuous across chunked events (chunk 1 →
  // 0..19, chunk 2 → 20..). `locationId` sets each item's location_id. No
  // 20-cap here anymore — chunking lives in view_item_list().
  function _itemsArray(products, listName, collectionTitle, startIndex, locationId) {
    if (!products || !products.length) return [];
    startIndex = startIndex || 0;
    var out = [];
    for (var i = 0; i < products.length; i += 1) {
      var item = _itemFromProduct(products[i], {
        listName: listName,
        collectionTitle: collectionTitle,
        index: startIndex + i,
        locationId: locationId
      });
      if (item) out.push(item);
    }
    return out;
  }

  // -------------------------------------------------------------------
  // Klaviyo behavioral tracking (Atlas trackKlaviyo.js parity). Fires
  // alongside the GA4 events at the same choke points. Payload keys
  // match Atlas: Name / ProductID / Categories / ImageURL / URL /
  // Brand / Price / CompareAtPrice / Size.
  // -------------------------------------------------------------------

  function _klaviyo(eventName, product, variant) {
    try {
      if (!product || !window.klaviyo || typeof window.klaviyo.push !== 'function') return;
      variant = variant || {};
      var price = (variant.price != null ? variant.price : product.price) || 0;
      if (typeof price === 'string') price = parseFloat(price);
      if (price > 1000) price = price / 100;
      var compare = variant.compare_at_price || product.compare_at_price || 0;
      if (typeof compare === 'string') compare = parseFloat(compare);
      if (compare && compare > 1000) compare = compare / 100;
      // ImageURL must be an ABSOLUTE https URL — Klaviyo renders it as a remote
      // <img src> in email templates, so a relative/protocol-relative value or
      // an empty string builds a broken link. Fallback chain: explicit
      // product.image (set by every view_item caller) → variant image →
      // featured_image → first product image.
      var img = (typeof product.image === 'string' && product.image) ||
                (variant.featured_image && (variant.featured_image.src || variant.featured_image)) ||
                (product.featured_image && (product.featured_image.src || product.featured_image)) ||
                (product.images && product.images[0]) || '';
      if (typeof img !== 'string') img = '';
      if (img.indexOf('//') === 0) {
        img = 'https:' + img;                    // protocol-relative → https
      } else if (img.charAt(0) === '/') {
        img = window.location.origin + img;      // root-relative → absolute
      }
      var url = product.url || (product.handle ? '/products/' + product.handle : window.location.pathname);
      if (String(url).indexOf('http') !== 0) url = window.location.origin + url;
      var category = (product.metafields && product.metafields.BP_ProductAttributes && product.metafields.BP_ProductAttributes.bp_department) ||
                     product.product_type || '';
      var payload = {
        Name:           product.title || '',
        ProductID:      String(product.id || ''),
        Categories:     category ? [category] : [],
        URL:            url,
        Brand:          product.vendor || 'Boston Proper',
        Price:          Number((price || 0).toFixed(2)),
        CompareAtPrice: (compare && compare > price) ? Number(compare.toFixed(2)) : null,
        Size:           variant.option2 || ''
      };
      // Only send ImageURL when we resolved a real absolute URL — never an
      // empty string (that renders a broken <img> in Klaviyo email templates).
      if (img) payload.ImageURL = img;
      window.klaviyo.push(['track', eventName, payload]);
    } catch (_) { /* Klaviyo not ready — non-fatal */ }
  }

  // -------------------------------------------------------------------
  // Public events — GA4 ecommerce essentials
  // -------------------------------------------------------------------

  function view_item(variant, product, eventLocation) {
    if (!product) return;
    var loc = eventLocation || 'Product Detail';
    var item = _itemFromProduct(product, { variant: variant, locationId: loc });
    // Spec: value + currency nested INSIDE event_details (not siblings).
    var ed = _eventDetails(loc);
    ed.value = (item || {}).price || 0;
    ed.currency = CURRENCY;
    _pushWithInventory('view_item', {
      event_details: ed,
      user_details:  _userDetails(),
      items:    [item]
    });
    _klaviyo('Viewed Product', product, variant);
  }

  // Fires ONE view_item_list event per group of ITEMS_MAX (20) products,
  // so a PLP showing 24 emits event 1 (products 0..19) + event 2 (20..23)
  // instead of only the first 20. `index` continues across events (see
  // _itemsArray). `locationId` overrides each item's location_id (PLP →
  // "Product Listing"); item_list_name / _eventDetails keep `listName`.
  // `startIndex` offsets the GA4 `index` of the FIRST product in this call,
  // so a PLP that reveals products in lazy-loaded batches keeps `index`
  // continuous ACROSS separate view_item_list events (batch 1 → 0.., batch 2
  // → continues where batch 1 left off). Defaults to 0 for one-shot callers.
  // `eventLocation` sets event_details.event_location INDEPENDENTLY of the
  // list name (mirrors select_item, where the surface — "Product Listing" /
  // "Search Experience" — differs from the browse/search list label). Defaults
  // to `listName` so the collection PLP (event_location === item_list_name ===
  // "Site Browse") is unchanged; the search page passes "Search Experience".
  // `searchTerm`, when set, adds event_details.search_term (mirrors select_item)
  // so the search page's list impression carries the typed query.
  function view_item_list(items, listName, listId, locationId, startIndex, eventLocation, searchTerm) {
    if (!items || !items.length) return;
    var name = listName || _locationFromPath();
    var loc = eventLocation || name;
    var base = startIndex || 0;
    var doPush = function () {
      for (var start = 0; start < items.length; start += ITEMS_MAX) {
        var chunk = items.slice(start, start + ITEMS_MAX);
        var ed = _eventDetails(loc);
        if (searchTerm) ed.search_term = searchTerm;
        _push('view_item_list', {
          event_details:  ed,
          user_details:   _userDetails(),
          item_list_name: name,
          item_list_id:   listId || '',
          items:          _itemsArray(chunk, name, '', base + start, locationId)
        });
      }
    };
    // BR grid cards carry no variant id / department / inventory. ONE batched
    // Storefront lookup (aliased by handle) enriches the whole grid so
    // item_shopify_variant / item_category / item_stock_qty populate — without
    // adding anything to the BR feed. Results are cached on window.__bpTrackData
    // so a subsequent select_item on the same card reads them SYNCHRONOUSLY
    // (no click-time fetch that would race the PDP navigation). Falls straight
    // through to the push if the helper/handles/lookup aren't available.
    var enrich = window.bpColorSiblings && window.bpColorSiblings.fetchTrackData;
    var handles = items.map(function (p) { return p && p.handle; }).filter(Boolean);
    if (!enrich || !handles.length) { doPush(); return; }
    enrich(handles).then(function (map) {
      if (!map) return;
      window.__bpTrackData = window.__bpTrackData || {};
      items.forEach(function (p) {
        var d = p && p.handle && map[p.handle];
        if (!d) return;
        window.__bpTrackData[p.handle] = d;
        p.selected_variant = p.selected_variant || {};
        if (d.variantId) p.selected_variant.id = d.variantId;
        if (d.quantityAvailable != null) p.selected_variant.inventory_quantity = d.quantityAvailable;
        if (d.color) p.selected_variant.option1 = d.color;   // → item_category4 (Color)
        if (d.size) p.selected_variant.option2 = d.size;     // → item_category5 (Size)
        if (d.department) {
          p.metafields = p.metafields || {};
          p.metafields.BP_ProductAttributes = p.metafields.BP_ProductAttributes || {};
          p.metafields.BP_ProductAttributes.bp_department = d.department;
        }
      });
    }).catch(function () { /* enrich best-effort */ }).then(doPush);
  }

  // `locationId` overrides each item's location_id independently of
  // item_list_name (PLP cards → item_list_name "Site Browse" but
  // location_id "Product Listing", matching view_item_list).
  function select_item(product, listName, index, locationId, searchTerm) {
    if (!product) return;
    _saveListContext(product, listName);
    // event_location follows the SURFACE (locationId = "Product Listing" on the
    // PLP, "Search Experience" in search) so it matches location_id, per QA.
    // item_list_name stays the GA4 list name ("Site Browse" / "Search Results")
    // so select_item links to its view_item_list in the funnel.
    var loc = locationId || listName || _locationFromPath();
    var ed = _eventDetails(loc);
    // Search experience clicks carry the term the shopper typed (GA4 QA #2 —
    // there's no search landing page, so search insight comes from the click).
    if (searchTerm) ed.search_term = searchTerm;
    _push('select_item', {
      event_details: ed,
      user_details:  _userDetails(),
      item_list_name: listName || _locationFromPath(),
      items: [_itemFromProduct(product, { listName: listName, index: index, locationId: locationId })]
    });
  }

  function add_to_cart(variant, product, available, eventLocation, parentProductId) {
    if (!product) return;
    var ctx = _getListContext(variant);
    // locationId follows the event surface (Quickview / Cart Flyout / Cart /
    // Product Detail) so each item's location_id matches event_location.
    var item = _itemFromProduct(product, { variant: variant, locationId: eventLocation, listId: eventLocation });
    if (item) {
      // item_list_name carries the source list (Site Browse / Search Results);
      // item_list_id/location_id stay the SURFACE (eventLocation) per GA4 QA #2,
      // so DON'T let the saved card context overwrite item_list_id here.
      if (ctx && ctx.list_name) item.item_list_name = ctx.list_name;
      if (available === false)  item.item_stock_message = 'Out of Stock';
      // item_parent_id stays the inv5 SKU (Atlas parity); parentProductId
      // (the Shopify product id) is carried by item_shopify_id instead.
    }
    // Spec: value + currency nested INSIDE event_details (not siblings).
    var ed = _eventDetails(eventLocation);
    ed.value = (item && item.price) || 0;
    ed.currency = CURRENCY;
    _pushWithInventory('add_to_cart', {
      event_details: ed,
      user_details:  _userDetails(),
      items:    item ? [item] : []
    });
    _klaviyo('Added to Cart', product, variant);
  }

  function remove_from_cart(item, eventLocation) {
    if (!item) return;
    var loc = eventLocation || 'Cart';
    // Item may be a Shopify cart line — normalize to a product-shaped object.
    var product = {
      id:         item.product_id || item.id,
      title:      item.product_title || item.title,
      vendor:     item.vendor,
      product_type: item.product_type
    };
    var variant = {
      // variant id → item_shopify_variant (was missing: cart lines carry it
      // on variant_id / id, but the old normalization dropped it).
      id:       item.variant_id || item.id,
      sku:      item.sku,
      price:    item.price,
      available: true,   // a line in the cart is purchasable → "In Stock"
      option1:  (item.options_with_values && item.options_with_values[0] && item.options_with_values[0].value) || item.variant_options && item.variant_options[0],
      option2:  (item.options_with_values && item.options_with_values[1] && item.options_with_values[1].value) || item.variant_options && item.variant_options[1]
    };
    // Spec: value + currency nested INSIDE event_details (not siblings).
    var ed = _eventDetails(loc);
    ed.value = ((variant.price || 0) * (item.quantity || 1)) / 100;
    ed.currency = CURRENCY;
    _pushWithInventory('remove_from_cart', {
      event_details: ed,
      user_details:  _userDetails(),
      items:    [_itemFromProduct(product, { variant: variant, quantity: item.quantity, locationId: loc })]
    });
  }

  // First applied discount code on a /cart.js cart, or null. Shopify's
  // cart.js exposes codes in different shapes across versions — check them
  // all defensively.
  function _cartCoupon(cart) {
    try {
      if (cart.discount_codes && cart.discount_codes.length) {
        var codes = cart.discount_codes.map(function (d) { return d.code || d.title; }).filter(Boolean);
        if (codes.length) return codes.join(',');
      }
      var apps = cart.discount_applications || cart.cart_level_discount_applications || [];
      for (var i = 0; i < apps.length; i += 1) {
        if (apps[i] && (apps[i].title || apps[i].code)) return apps[i].title || apps[i].code;
      }
    } catch (_) { /* ignore */ }
    return null;
  }

  // /cart.js → GA4 view_cart payload. `eventLocation` distinguishes the
  // flyout ("Cart Flyout") from the cart page ("Cart") — the caller passes
  // it; default resolves from the path.
  function view_cart(eventLocation) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var p = window.location.pathname;
        var loc = eventLocation || ((p === '/cart' || p.indexOf('/cart?') === 0) ? 'Cart' : 'Cart Flyout');
        var items = cart.items.map(function (line, i) {
          var product = {
            id:           line.product_id,
            title:        line.product_title || line.title,
            vendor:       line.vendor,
            product_type: line.product_type
          };
          var variant = {
            id:      line.variant_id || line.id,   // → item_shopify_variant (was missing)
            sku:     line.sku,
            price:   line.price,
            available: true,   // a line in the cart is purchasable → "In Stock"
            option1: line.options_with_values && line.options_with_values[0] && line.options_with_values[0].value,
            option2: line.options_with_values && line.options_with_values[1] && line.options_with_values[1].value
          };
          // Spec: index starts at 0 (was i + 1). locationId follows loc.
          return _itemFromProduct(product, { variant: variant, quantity: line.quantity, index: i, locationId: loc });
        }).filter(Boolean);

        if (window.__DL__) window.__DL__.cart = cart;

        // Spec: value/currency + cart_qty, sku_count, checkout_section,
        // discount, coupon all nested INSIDE event_details.
        var ed = _eventDetails(loc);
        ed.value           = cart.total_price / 100;
        ed.currency        = CURRENCY;
        ed.cart_qty        = cart.item_count;
        ed.sku_count       = cart.items.length;
        ed.checkout_section = 'View Cart';
        ed.discount        = (cart.total_discount || 0) / 100;
        ed.coupon          = _cartCoupon(cart);

        _pushWithInventory('view_cart', {
          event_details: ed,
          user_details:  _userDetails(),
          items:    items
        });
      })
      .catch(function () { /* silent — analytics never breaks the user flow */ });
  }

  // -------------------------------------------------------------------
  // Variant micro-events
  // -------------------------------------------------------------------

  function size_change(sizeValue, eventLocation) {
    // Default to the page type (_locationFromPath) so a size change inside the
    // quick-view FLYOUT on a PLP reports "Site Browse", the PDP reports
    // "Product Detail", etc. Callers may still pass an explicit location.
    _push('size_change', {
      event_details: _eventDetails(eventLocation || _locationFromPath()),
      user_details:  _userDetails(),
      size_value:    String(sizeValue || '')
    });
  }

  function color_change(colorValue, eventLocation) {
    _push('color_change', {
      event_details: _eventDetails(eventLocation || 'Product Detail'),
      user_details:  _userDetails(),
      color_value:   String(colorValue || '')
    });
  }

  // -------------------------------------------------------------------
  // Wishlist / favorites — Lana-new, not in Atlas
  // -------------------------------------------------------------------

  // Mirrors add_to_cart 1:1 so the wishlist item[] carries the SAME schema the
  // container already resolves (item_id inv7, item_variant full SKU,
  // item_parent_id inv5, item_shopify_id parent product id, item_shopify_variant
  // variant id, item_category4 Color / item_category5 Size, price = selling
  // price, item_original_price = compare-at). `eventLocation` defaults to the
  // Page Type resolved from the path (Product Detail / Site Browse / Homepage /
  // Cart / …), the same event_location scheme every other BP event uses — no
  // hardcoded "Wishlist" surface. value + currency are nested INSIDE
  // event_details (spec — not top-level siblings). _pushWithInventory fills
  // item_stock_qty from the live Storefront lookup (the wishlist payload has no
  // inventory number, just like cart lines).
  function add_to_wishlist(product, eventLocation) {
    if (!product) return;
    var loc = eventLocation || _locationFromPath();
    var item = _itemFromProduct(product, { locationId: loc, listId: loc });
    var ed = _eventDetails(loc);
    ed.value    = (item && item.price) || 0;
    ed.currency = CURRENCY;
    _pushWithInventory('add_to_wishlist', {
      event_details: ed,
      user_details:  _userDetails(),
      items:    item ? [item] : []
    });
  }

  function remove_from_wishlist(product, eventLocation) {
    if (!product) return;
    var loc = eventLocation || _locationFromPath();
    var item = _itemFromProduct(product, { locationId: loc, listId: loc });
    var ed = _eventDetails(loc);
    ed.value    = (item && item.price) || 0;
    ed.currency = CURRENCY;
    _pushWithInventory('remove_from_wishlist', {
      event_details: ed,
      user_details:  _userDetails(),
      items:    item ? [item] : []
    });
  }

  // -------------------------------------------------------------------
  // Account
  // -------------------------------------------------------------------

  function sign_up(method) {
    _push('sign_up', {
      event_details: _eventDetails('Account'),
      user_details:  _userDetails(),
      method:        method || 'email'
    });
  }

  function login(method) {
    _push('login', {
      event_details: _eventDetails('Account'),
      user_details:  _userDetails(),
      method:        method || 'email'
    });
  }

  // -------------------------------------------------------------------
  // Search + PLP funnel (Lana-new — Atlas had none)
  // -------------------------------------------------------------------

  function search(query, resultsCount) {
    _push('search', {
      event_details:  _eventDetails('Search Results'),
      user_details:   _userDetails(),
      search_term:    String(query || ''),
      results_count:  resultsCount == null ? null : Number(resultsCount)
    });
  }

  function filter_apply(filters, listName) {
    _push('filter_apply', {
      event_details:  _eventDetails(listName || _locationFromPath()),
      user_details:   _userDetails(),
      item_list_name: listName || _locationFromPath(),
      filters:        filters || {}
    });
  }

  function sort_change(sortBy, listName) {
    _push('sort_change', {
      event_details:  _eventDetails(listName || _locationFromPath()),
      user_details:   _userDetails(),
      item_list_name: listName || _locationFromPath(),
      sort:           String(sortBy || '')
    });
  }

  // -------------------------------------------------------------------
  // Expose
  // -------------------------------------------------------------------

  window.bpTrack = {
    view_item:             view_item,
    view_item_list:        view_item_list,
    select_item:           select_item,
    add_to_cart:           add_to_cart,
    remove_from_cart:      remove_from_cart,
    view_cart:             view_cart,
    size_change:           size_change,
    color_change:          color_change,
    add_to_wishlist:       add_to_wishlist,
    remove_from_wishlist:  remove_from_wishlist,
    sign_up:               sign_up,
    login:                 login,
    search:                search,
    filter_apply:          filter_apply,
    sort_change:           sort_change,
    // exposed internals for page tracker + extensions
    _push:             _push,
    _userDetails:      _userDetails,
    _eventDetails:     _eventDetails,
    _itemsArray:       _itemsArray,
    _itemFromProduct:  _itemFromProduct,
    _saveListContext:  _saveListContext,
    _getListContext:   _getListContext
  };
})();
